import { clamp, pearson } from './statistics'

export interface ExposureItem {
  id: string
  label: string
  group: string
  amount: number
}

export interface GroupExposure {
  group: string
  amount: number
  pctOfBankroll: number
  items: ExposureItem[]
}

export function aggregateExposure(items: readonly ExposureItem[], bankroll: number): GroupExposure[] {
  const byGroup = new Map<string, ExposureItem[]>()
  for (const item of items) {
    const list = byGroup.get(item.group) ?? []
    list.push(item)
    byGroup.set(item.group, list)
  }
  const out: GroupExposure[] = []
  for (const [group, groupItems] of byGroup) {
    const amount = groupItems.reduce((acc, i) => acc + i.amount, 0)
    out.push({
      group,
      amount,
      pctOfBankroll: bankroll > 0 ? amount / bankroll : 0,
      items: groupItems,
    })
  }
  return out.sort((a, b) => b.amount - a.amount)
}

export type WarningLevel = 'info' | 'warning' | 'danger'

export interface ExposureWarning {
  level: WarningLevel
  message: string
  group?: string
  pct?: number
}

export interface ExposureLimits {
  maxGroupPct?: number
  maxSinglePct?: number
  maxCorrelatedPct?: number
}

export function correlatedExposureWarnings(
  items: readonly ExposureItem[],
  bankroll: number,
  limits: ExposureLimits = {},
): ExposureWarning[] {
  const maxGroupPct = limits.maxGroupPct ?? 0.2
  const maxSinglePct = limits.maxSinglePct ?? 0.1
  const warnings: ExposureWarning[] = []

  for (const item of items) {
    const pct = bankroll > 0 ? item.amount / bankroll : 0
    if (pct > maxSinglePct) {
      warnings.push({
        level: pct > maxSinglePct * 1.5 ? 'danger' : 'warning',
        message: `${item.label} is ${(pct * 100).toFixed(1)}% of bankroll (cap ${(maxSinglePct * 100).toFixed(0)}%).`,
        group: item.group,
        pct,
      })
    }
  }

  for (const exposure of aggregateExposure(items, bankroll)) {
    if (exposure.items.length > 1 && exposure.pctOfBankroll > maxGroupPct) {
      warnings.push({
        level: exposure.pctOfBankroll > maxGroupPct * 1.5 ? 'danger' : 'warning',
        message: `Correlated cluster "${exposure.group}" holds ${(exposure.pctOfBankroll * 100).toFixed(1)}% across ${exposure.items.length} positions (cap ${(maxGroupPct * 100).toFixed(0)}%). One shock hits them together.`,
        group: exposure.group,
        pct: exposure.pctOfBankroll,
      })
    }
  }

  return warnings
}

export function correlationMatrix(series: Record<string, readonly number[]>): Record<string, Record<string, number>> {
  const keys = Object.keys(series)
  const matrix: Record<string, Record<string, number>> = {}
  for (const a of keys) {
    matrix[a] = {}
    for (const b of keys) {
      matrix[a][b] = a === b ? 1 : clamp(pearson(series[a], series[b]), -1, 1)
    }
  }
  return matrix
}
